import { useOne, useShow, useTranslate } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { Button, Space, theme } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import type { OAuthTokenOut, UserOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { useOAuthTokenActions } from '@/hooks/iam/useOAuthTokenActions';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { OAUTH_TOKEN_DEFAULT_TAB, OAUTH_TOKEN_SHOW_TABS } from '../constants';
import { OAuthTokenShowHeader } from './OAuthTokenShowHeader';
import { OAuthTokenGeneralTab } from './OAuthTokenGeneralTab';

export const OAuthTokenShow = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { query } = useShow<OAuthTokenOut>({ resource: 'iam.oauth_tokens' });
  const { data, isLoading } = query;
  const record = data?.data;
  const actions = useOAuthTokenActions({ tokenId: record?.id });

  const { result: user } = useOne<UserOut>({
    resource: 'iam.users',
    id: record?.user_id ?? 0,
    queryOptions: { enabled: record?.user_id != null },
  });

  useRecordDocumentTitle(
    'iam.oauth_tokens',
    record ? record.device_name || `#${record.id}` : undefined
  );

  const [activeTab, onTabChange] = useShowTab(
    OAUTH_TOKEN_SHOW_TABS,
    OAUTH_TOKEN_DEFAULT_TAB
  );

  return (
    <Show
      isLoading={isLoading}
      contentProps={bareShowContentProps}
      headerButtons={({ defaultButtons }) => (
        <Space>
          {defaultButtons}
          {record && !record.revoked && actions.revoke.canExecute && (
            <Button
              danger
              icon={<StopOutlined />}
              onClick={actions.revoke.mutate}
              loading={actions.revoke.isLoading}
            >
              {t('iam.oauth_tokens.actions.revoke')}
            </Button>
          )}
        </Space>
      )}
    >
      {record && (
        <>
          <OAuthTokenShowHeader
            record={record}
            user={user}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <OAuthTokenGeneralTab record={record} user={user} />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
